// @ts-nocheck
import _ from 'lodash';
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import {
  makeStyles,
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Icon,
  Radio,
  RadioGroup,
  TextField,
  FormLabel,
  Button,
  Typography,
  InputAdornment,
  FormGroup,
  Checkbox,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDialog, useIfMounted } from '../../../../common/hooks';
import { StringUtils } from '../../../../common/utils';
import helperImage from '../../../../assets/kampagnebetingelse.png';
import ValidationUtils, { PROMOTION_CODE_PATTERN } from '../../../../common/utils/ValidationUtils';

const useStyles = makeStyles(theme => ({
  container: {
    flex: '1',
    display: 'flex',
  },
  form: {
    flex: '1',
    display: 'flex',
    flexDirection: 'column',
    '& .MuiFormControl-root': {
      minHeight: '78px',
    },
    '& .MuiFormGroup-root': {
      minHeight: '78px',
      paddingTop: '4px',
    },
  },
  formContent: {
    flex: '1',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 3),
    overflow: 'auto',
  },
  switch: {
    margin: '-4px 0 0 0',
    alignItems: 'flex-start',
    '& .MuiSwitch-root': {
      marginLeft: '-8px',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
  button: {
    margin: theme.spacing(1),
    '&:last-child': {
      marginRight: '0',
    },
  },
  validationError: {
    color: theme.palette.error.main,
  },
  imageContainer: {
    display: 'flex',
    flexDirection: 'row-reverse',
  },
  helperImage: {
    width: '67%',
    paddingLeft: '16px',
  },
}));

type PromotionForm = {
  type: string;
  code: string;
  description: string;
  discountDuration: number;
  discountType: string;
  discountValue: number;
  restrictedProducts: Array<number>;
  changeProducts: Array<number>;
}

export const PromotionForm = React.forwardRef((props, ref) => {
  const {
    promotion,
    // products,
    onSave,
    onCancel,
    editMode,
    actionsDisabled,
    cancelButtonText,
    cancelButtonIcon,
    saveButtonText,
    saveButtonIcon,
    onClosePromotion,
    submit,
  } = props;
  const { showDialog } = useDialog();
  const ifMounted = useIfMounted();

  const [editable, setEditable] = useState(!editMode);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const isActive = promotion?.state === 'ACTIVE';
  const hasPeriods = promotion?.periods.length > 0;
  const [t] = useTranslation();

  const [discountType, setDiscountType] = useState(_.defaultTo(promotion?.discountType, 'PERCENT'));
  const formatDiscountValue = (value) => {
    return value ?
    (discountType === 'AMOUNT' ? 
      _.defaultTo(((value) / 100).toFixed(2).replace('.', ',')) :
      value) :
    '';
  }
  const [discountValue, setDiscountValue] = useState(formatDiscountValue(promotion?.discountValue));
  const formatDiscountValueSuffix = () => {
    return discountType === 'PERCENT' ?
     '%' :
     'Kr.';
  };

  const { control, errors, formState, reset, register, handleSubmit, watch, setValue, setError, clearErrors } =
    useForm<PromotionForm>({
      defaultValues: {
        type: _.defaultTo(promotion?.type, 'SALE'),
        code: _.defaultTo(promotion?.code, ''),
        description: _.defaultTo(promotion?.description, ''),
        discountType: discountType,
        discountDuration: _.defaultTo(promotion?.discountDuration, 0),
        discountValue: discountValue,
        restrictedProducts: _.defaultTo(promotion?.products?.RESTRICTION, []),
        changeProducts: _.defaultTo(promotion?.products?.CHANGE, []),
      },
    });
  const type = watch('type', _.defaultTo(promotion?.type, 'SALE'));
  const selectedProducts = watch('restrictedProducts', _.defaultTo(promotion?.products?.RESTRICTION, []));
  const selectedChanges = watch('changeProducts', _.defaultTo(promotion?.products?.CHANGE, []));
  const conditionsRequired = type === 'EVENT';

  const classes = useStyles();
  const { isDirty, isSubmitting } = formState;

  const products = [1250, 1194, 1251, 1134, 1262, 1263];
  const restrictedProductsLogic = {
    1250: [ 1251, 1134, 1262, 1263, 1194 ],
    1194: [ 1134, 1262, 1263, 1251 ],
    1251: [ 1134, 1262, 1263 ],
    1134: [ 1262, 1263 ],
    1262: [ 1263 ],
    1263: []
  };

  const isOptionEnabled = (option) => {
    if(type !== 'CHANGE')
      return false;

    if(selectedProducts.length === 0)
      return true;

    const enabledOptions = selectedProducts.reduce((acc, selectedOption) => {
      if (restrictedProductsLogic[selectedOption]) {
        acc.push(...restrictedProductsLogic[selectedOption]);
      }
      return acc;
    }, []);
    return enabledOptions.includes(option);
  };

  const validateDiscountFormat = () => {
    return discountType === 'PERCENT' ? {
      value: /^(100|[1-9]?[0-9])$/,
      message: t('promotion.promotionDiscountValuePercent'),
    } :
    {
      value: /^[0-9]*[0-9][,][0-9]{2}$/,
      message: t('promotion.promotionDiscountValueDecimal'),
    }
  }

  const validateDiscountValue = value => {
    if (value === '0' || value === '') {
      return t('promotion.promotionValidDiscountValue');
    }
    return true;
  };

  const validateDiscountDuration = value => {
    return ValidationUtils.validateDiscountDuration(value, t);
  };

  const validateProducts = () => {
    let failed = false;
    if (selectedProducts.length === 0) {
      setError('restrictedProductsError', {
        type: 'manual',
        message: 'At least one product must be selected',
      });
      failed = true;
    } else {
      clearErrors('restrictedProductsError');
    }
    if (type === 'CHANGE') {
      if (selectedChanges.length === 0) {
        setError('changeProductsError', {
          type: 'manual',
          message: 'At least one product must be selected when CHANGE is selected',
        });
        failed = true;
      } else {
        clearErrors('changeProductsError');
      }      
    } else {
      clearErrors('changeProductsError');
    }  
    return !failed;
  };

  const handleCancel = () => {
    if (editMode && editable) {
      reset();
      setEditable(false);
    } else {
      onCancel();
    }
  };

  const onSubmit = async data => {
    data.discountValue = _.toNumber(data.discountValue.replace(',', '')) ?? 0;
    data.discountDuration = _.toNumber(data.discountDuration);
    
    try {
      setHasSubmitted(true);
      if (!validateProducts()) {
        return;
      }
      
      await onSave(data);
      ifMounted(() => {
        if (editMode) {
          setEditable(false);
        }
      });
    } catch (e) {
      // Error already handled
    }
  };

  const onError = errors => {
    setHasSubmitted(true);
    validateProducts();
  }

  const handleShowClosePromotionDialog = () => {
    showDialog({
      title: t('common.important'),
      content: <Typography>{t('promotion.promotionCloseConfirm')}</Typography>,
      onConfirm: onClosePromotion,
    });
  };

  React.useEffect(() => {
    if(editable && hasSubmitted) {
      validateProducts();
    }
  }, [type, selectedChanges, selectedProducts]);
  
  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmit)(),
  }));

  return (
    <Box id="PromotionForm-container" className={classes.container}>
      <form id="PromotionForm-form" className={classes.form} onSubmit={handleSubmit(onSubmit, onError)}>
        <Box className={classes.formContent}>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <TextField
                id="PromotionForm-code"
                name="code"
                label={t('promotion.promotionCode')}
                variant="outlined"
                defaultValue={promotion?.code}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('promotion.promotionCodeRequired'),
                  pattern: {
                    value: PROMOTION_CODE_PATTERN,
                    message: t('promotion.promotionCodeInvalid'),
                  },
                })}
                error={!!errors.code}
                helperText={errors.code?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={8}>
              <TextField
                id="PromotionForm-description"
                name="description"
                label={t('promotion.promotionDescription')}
                variant="outlined"
                defaultValue={promotion?.description}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('promotion.promotionDescriptionRequired'),
                })}
                error={!!errors.description}
                helperText={errors.description?.message}
                disabled={!editable}
                fullWidth
                multiline
              />
            </Grid>
            <Grid item xs={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('promotion.promotionType')}</FormLabel>
                <Controller
                    name="type"
                    control={control}
                    defaultValue={promotion?.type}
                    render={params => (
                      <RadioGroup id="PromotionForm-promotionType"
                        value={params.value}
                        onChange={e => {
                          setValue('restrictedProducts', []);
                          setValue('changeProducts', []);
                          params.onChange(e);
                        }}
                      >
                        <FormControlLabel
                            value="SALE"
                            control={<Radio />}
                            label={t('promotion.promotionTypeSale')}
                            disabled={!editable}
                        />
                        {/* <FormControlLabel
                            value="SALE_PERSISTENCE"
                            control={<Radio />}
                            label={t('promotion.promotionTypeSalePersistance')}
                            disabled={!editable}
                        /> */}
                        <FormControlLabel
                            value="CHANGE"
                            control={<Radio />}
                            label={t('promotion.promotionTypeChange')}
                            disabled={!editable}
                        />
                        <FormControlLabel
                            value="EVENT"
                            control={<Radio />}
                            label={t('promotion.promotionTypeEvent')}
                            disabled={!editable}
                        />
                      </RadioGroup>
                    )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('promotion.promotionDiscountType')}</FormLabel>
                <Controller
                    name="discountType"
                    control={control}
                    defaultValue={discountType}
                    render={(params) => (
                      <RadioGroup id="PromotionForm-discountType"
                        value={params.value}
                        onChange={e => { setDiscountType(e.target.value); params.onChange(e); }}
                      >
                        <FormControlLabel
                            value="PERCENT"
                            control={<Radio />}
                            label={t('promotion.promotionDiscountTypePercent')}
                            disabled={!editable}
                        />
                        <FormControlLabel
                            value="AMOUNT"
                            control={<Radio />}
                            label={t('promotion.promotionDiscountTypeAmount')}
                            disabled={!editable}
                        />
                      </RadioGroup>
                    )}
                />
              </FormControl>
            </Grid>
            <Grid item xs={3}>
              <TextField
                  id="PromotionForm-discountDuration"
                  name="discountDuration"
                  label={t('promotion.promotionDiscountDuration')}
                  variant="outlined"
                  defaultValue={promotion?.discountDuration ?? ''}
                  InputLabelProps={{ shrink: editMode }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">dage</InputAdornment>,
                  }}
                  inputRef={register({
                    required: t('promotion.promotionDiscountDurationRequired'),
                    pattern: {
                      value: /^[0-9]+$/,
                      message: t('promotion.promotionValidDaysInteger'),
                    },
                    validate: validateDiscountDuration
                  })}
                  error={!!errors.discountDuration}
                  helperText={errors.discountDuration?.message}
                  disabled={!editable}
                  fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                id="PromotionForm-discountValue"
                name="discountValue"
                label={t('promotion.promotionDiscountValue')}
                variant="outlined"
                defaultValue={discountValue}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{formatDiscountValueSuffix()}</InputAdornment>,
                }}
                InputLabelProps={{ shrink: editMode }}
                inputRef={register({
                  required: t('promotion.promotionDiscountValueRequired'),
                  pattern: validateDiscountFormat(),
                  validate: validateDiscountValue,
                })}
                onChange={(e) => {
                  setDiscountValue(formatDiscountValue(e.target.value));
                }}
                error={!!errors.discountValue}
                helperText={errors.discountValue?.message}
                disabled={!editable}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={conditionsRequired ? 9 : 12}>
                <Grid container spacing={2}>
                  {conditionsRequired && (
                    <Grid item xs={12}>
                      <TextField
                        id="PromotionForm-conditions"
                        name="conditions"
                        label={t('promotion.promotionConditions')}
                        variant="outlined"
                        defaultValue={promotion?.conditions}
                        InputLabelProps={{ shrink: editMode }}
                        inputRef={register({
                          required: conditionsRequired && t('promotion.promotionConditionsRequired'),
                        })}
                        error={!!errors.conditions}
                        helperText={errors.conditions?.message}
                        disabled={!editable}
                        fullWidth
                        multiline
                      />
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <FormControl component="fieldset" error={!!errors.restrictedProducts}>
                      <FormLabel component="legend">{t('promotion.promotionRestrictedProducts')}</FormLabel>
                      <FormGroup>
                        {products.map((option) => (
                          <Controller
                            key={option}
                            name="restrictedProducts"
                            control={control}
                            render={params => {
                              const isError = editable && !!errors.restrictedProductsError;
                              return (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      className={`${option}`}
                                      checked={selectedProducts.includes(option)}
                                      onChange={(e) => {
                                        const isMultiSelect = type === 'SALE';
                                        let newValue;
                                        if (e.target.checked) {
                                          newValue = isMultiSelect
                                            ? [ ...selectedProducts, option ]
                                            : [ option ];
                                        } else {
                                          newValue = selectedProducts.filter((value) => value !== option);
                                        }
                                        params.onChange(newValue);
                                        if(!isMultiSelect && selectedChanges.length)
                                          setValue('changeProducts', []);
                                      }}
                                      disabled={!editable}
                                  className={
                                    isError ? classes.validationError : ''
                                  }
                                    />
                                  }
                                  label={StringUtils.getTranslation(
                                    t,
                                    'product.types',
                                    option,
                                    ""
                                  )}
                                />
                              );
                            }}
                          />
                        ))}
                      {errors.restrictedProductsError && (<p className='MuiFormHelperText-root MuiFormHelperText-contained Mui-error MuiFormHelperText-filled'>{errors.restrictedProductsError.message}</p>)}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl component="fieldset" error={!!errors.changeProducts}>
                    <FormLabel component="legend">{t('promotion.promotionChangeProducts')}</FormLabel>
                      <FormGroup>
                        {products.map((option) => (
                          <Controller
                            key={option}
                            name="changeProducts"
                            control={control}
                            render={ params  => {
                              const isError = !!errors.changeProductsError && editable && isOptionEnabled(option);
                              return (
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      className={`${option}`}
                                      checked={selectedChanges.includes(option)}
                                      onChange={(e) => {
                                        const newValue = e.target.checked
                                          ? [ option ]
                                          : selectedChanges.filter((value) => value !== option);
                                        params.onChange(newValue);
                                      }}
                                      disabled={!(editable && isOptionEnabled(option))}
                                      className={
                                        isError ? classes.validationError : ''
                                      }
                                    />
                                  }
                                  label={StringUtils.getTranslation(
                                    t,
                                    'product.types',
                                    option,
                                    ""
                                  )}
                                />
                              );
                            }}
                          />
                        ))}
                      {errors.changeProductsError && (<p className='MuiFormHelperText-root MuiFormHelperText-contained Mui-error MuiFormHelperText-filled'>{errors.changeProductsError.message}</p>)}
                      </FormGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>
              {conditionsRequired && 
                (<Grid item xs={3}>
                  <div className={classes.imageContainer}>
                    <img className={classes.helperImage} src={helperImage} alt="helper image" />
                  </div>
                </Grid>
              )}
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box id='PromotionForm-actions' className={classes.actions}>
          <Button
            id="PromotionForm-cancel"
            className={classes.button}
            startIcon={<Icon>{!editable ? cancelButtonIcon : 'cancel'}</Icon>}
            onClick={handleCancel}
            disabled={actionsDisabled || isSubmitting}
          >
            <Typography variant="button" noWrap>
              {!editable ? cancelButtonText : t('common.cancel')}
            </Typography>
          </Button>
          {isActive && !hasPeriods && (
            <Button
              id="PromotionForm-close"
              className={classes.button}
              color="primary"
              startIcon={<Icon>lock</Icon>}
              onClick={handleShowClosePromotionDialog}
              disabled={actionsDisabled}
            >
              <Typography variant="button" noWrap>
                {t('promotion.promotionClose')}
              </Typography>
            </Button>
          )}
          {!editable && isActive && !hasPeriods ? (
            <Button
              id="PromotionForm-edit"
              key="edit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>edit</Icon>}
              onClick={() => setEditable(true)}
              disabled={actionsDisabled || isSubmitting}
            >
              <Typography variant="button" noWrap>
                {t('common.edit')}
              </Typography>
            </Button>
          ) : (
            <Button
              id="PromotionForm-save"
              key="save"
              type="submit"
              color="primary"
              className={classes.button}
              startIcon={<Icon>{_.defaultTo(saveButtonIcon, 'save')}</Icon>}
              disabled={actionsDisabled || isSubmitting || !isDirty}
            >
              <Typography variant="button" noWrap>
                {_.defaultTo(saveButtonText, t('common.save'))}
              </Typography>
            </Button>
          )}
        </Box>
      </form>
    </Box>
  );
});

PromotionForm.defaultProps = {
  promotion: undefined,
  cancelButtonText: undefined,
  cancelButtonIcon: undefined,
  saveButtonText: undefined,
  saveButtonIcon: undefined,
  actionsDisabled: undefined,
  editMode: undefined,
};

PromotionForm.propTypes = {
  promotion: PropTypes.shape({
    code: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    discountDuration: PropTypes.number,
    discountType: PropTypes.string,
    discountValue: PropTypes.number,
    description: PropTypes.string,
    conditions: PropTypes.string,
  }),
  cancelButtonText: PropTypes.string,
  cancelButtonIcon: PropTypes.string,
  saveButtonText: PropTypes.string,
  saveButtonIcon: PropTypes.string,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onClosePromotion: PropTypes.func,
  actionsDisabled: PropTypes.bool,
  editMode: PropTypes.bool,
  isActive: PropTypes.bool,
};

export default PromotionForm;
